import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, formatApiError } from "../lib/api";
import { Pen, CheckCircle2, Loader2, MessageCircle, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function SocialSignaturePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signedFullName, setSignedFullName] = useState("");
  const [place, setPlace] = useState("Santarém-PA");
  const [done, setDone] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    fetch(`${API}/social/sign/${token}`)
      .then((r) => { if (!r.ok) throw new Error("Link inválido ou expirado"); return r.json(); })
      .then((d) => {
        setData(d);
        setSignedFullName(d.name || "");
        if (d.signed) setDone(true);
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1A3620";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [data, done]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    if (e.touches && e.touches[0]) {
      return { x: (e.touches[0].clientX - rect.left) * sx, y: (e.touches[0].clientY - rect.top) * sy };
    }
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const start = (e) => { e.preventDefault(); isDrawing.current = true; const p = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!isDrawing.current) return; e.preventDefault(); const p = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(p.x, p.y); ctx.stroke(); hasDrawn.current = true; };
  const end = (e) => { e.preventDefault(); isDrawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
  };

  const submit = async () => {
    if (!hasDrawn.current) { toast.error("Por favor, faça sua assinatura na tela antes de enviar."); return; }
    if (!signedFullName.trim()) { toast.error("Confirme seu nome completo."); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const res = await fetch(`${API}/social/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature_dataurl: dataUrl, signed_full_name: signedFullName, place }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({ detail: "Erro" }));
        throw new Error(typeof j.detail === "string" ? j.detail : formatApiError({ response: { data: j } }));
      }
      const j = await res.json();
      setDone(true);
      // Refresh data to display signed state
      const r2 = await fetch(`${API}/social/sign/${token}`);
      if (r2.ok) setData(await r2.json());
      toast.success("Assinado! Protocolo " + j.protocol);
    } catch (err) {
      toast.error(err.message || "Falha ao assinar");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-[70vh] flex items-center justify-center text-forest-900/70">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
    </div>
  );

  if (!data) return (
    <div className="container mx-auto px-6 py-16 max-w-2xl text-center">
      <h1 className="font-display text-3xl text-forest-900">Link inválido</h1>
      <p className="mt-3 text-forest-900/70">Este link de assinatura não foi encontrado ou expirou.</p>
      <Link to="/" className="inline-block mt-6 px-5 py-2 bg-forest-700 text-cream-100">Voltar ao site</Link>
    </div>
  );

  const sigUrl = window.location.href;
  // approx — backend gives /api/social/requests/{id}/pdf?token=token; we don't have id here.
  // We use the sign endpoint response with the protocol embedded. For PDF, we offer to message Elen
  const waBody = encodeURIComponent(`Olá Elen! Acabei de assinar o termo da minha solicitação. Protocolo: ${data.protocol}. Link: ${sigUrl}`);
  const waElen = `https://wa.me/${data.responsavel?.whatsapp || "5593991493368"}?text=${waBody}`;

  if (done) {
    return (
      <div className="container mx-auto px-6 py-12 max-w-2xl" data-testid="social-sign-done">
        <div className="bg-cream-100 border border-forest-700 p-8">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-7 h-7 text-forest-700" />
            <h1 className="font-display text-3xl text-forest-900">Termo assinado!</h1>
          </div>
          <p className="mt-3 text-forest-900/80">
            Sua assinatura digital foi registrada com sucesso para o protocolo <strong className="font-mono">{data.protocol}</strong>.
          </p>
          <p className="mt-2 text-sm text-forest-900/70">
            O Setor Social vai analisar sua solicitação. Em caso de dúvidas, fale com a {data.responsavel?.nome || "Elen"} pelo WhatsApp.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href={`${API}/social/sign/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="signed-download-pdf"
               className="inline-flex items-center gap-2 px-5 py-2 bg-forest-700 text-cream-100 hover:bg-forest-900">
              Baixar termo em PDF
            </a>
            <a href={waElen} target="_blank" rel="noreferrer" data-testid="signed-wa-elen"
               className="inline-flex items-center gap-2 px-5 py-2 bg-[#25D366] text-white hover:opacity-90">
              <MessageCircle className="w-4 h-4" /> Avisar a Elen pelo WhatsApp
            </a>
            <Link to="/" className="px-5 py-2 border border-cream-400 text-forest-900 hover:bg-cream-200 inline-flex items-center">
              Voltar ao site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-10 max-w-3xl" data-testid="social-sign-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-forest-700 text-cream-100 flex items-center justify-center">
          <Pen className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700">Assinatura Digital</div>
          <h1 className="font-display text-3xl text-forest-900">Termo de Solicitação e Aceite</h1>
        </div>
      </div>

      <div className="bg-white border border-cream-400 p-5 mb-5 text-sm">
        <div className="font-mono text-forest-700">{data.protocol}</div>
        <div className="font-display text-xl text-forest-900 mt-1">{data.name}</div>
        <div className="text-forest-900/70 mt-1">CPF {data.cpf_masked} · {data.category_label} {data.community ? `· ${data.community}` : ""}</div>
        <div className="mt-3"><span className="text-xs uppercase tracking-[0.14em] text-forest-700">Tipo</span><div className="text-forest-900">{data.request_type}</div></div>
        <div className="mt-2"><span className="text-xs uppercase tracking-[0.14em] text-forest-700">Descrição</span><div className="text-forest-900 whitespace-pre-wrap">{data.description}</div></div>
      </div>

      <div className="bg-cream-100 border border-cream-400 p-5 mb-5 text-sm text-forest-900/85 leading-relaxed">
        Eu, <strong>{data.name}</strong>, CPF <strong>{data.cpf_masked}</strong>{data.community ? <>, residente na comunidade <strong>{data.community}</strong></> : null}, solicito apoio da COOMFLONA referente à demanda descrita acima. Declaro que as informações são verdadeiras e autorizo a COOMFLONA a utilizá-las para análise.
      </div>

      <div className="bg-white border border-cream-400 p-5">
        <div className="font-display text-lg text-forest-900 mb-2">Assine aqui com o dedo (no celular) ou com o mouse:</div>
        <canvas
          ref={canvasRef}
          width={900}
          height={260}
          data-testid="signature-canvas"
          className="w-full bg-white border-2 border-dashed border-forest-700/40 touch-none"
          style={{ touchAction: "none" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={clear} data-testid="sign-clear" className="px-3 py-1.5 text-sm border border-cream-400 text-forest-900 hover:bg-cream-200">
            Limpar assinatura
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Confirme seu nome completo</span>
            <input value={signedFullName} onChange={(e) => setSignedFullName(e.target.value)} data-testid="signed-name" className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-forest-900/70">Local</span>
            <input value={place} onChange={(e) => setPlace(e.target.value)} data-testid="signed-place" className="mt-2 w-full bg-cream-100 border border-cream-400 focus:border-forest-700 focus:outline-none p-3" />
          </label>
        </div>

        <Button onClick={submit} disabled={submitting} data-testid="sign-submit" className="w-full mt-5 bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none py-5 text-base">
          {submitting ? "Enviando..." : "Confirmar assinatura"}
        </Button>
      </div>
    </div>
  );
}
